import { ChangeEvent, FC, useState } from 'react'
import { TextInput, useTranslate } from 'react-admin'
import { useFormContext, useWatch } from 'react-hook-form'
import { Box, Button, ToggleButton, ToggleButtonGroup, Typography } from '@mui/material'
import UploadFileIcon from '@mui/icons-material/UploadFile'
import LinkIcon from '@mui/icons-material/Link'

// Create mode shows a URL/file toggle; Edit mode shows both fields (URL + optional file override).
interface OpenApiUrlFieldProps {
  /** Show URL/file toggle (Create mode). */
  showToggle?: boolean
}

const OpenApiUrlField: FC<OpenApiUrlFieldProps> = ({ showToggle = false }) => {
  const translate = useTranslate()
  const { setValue } = useFormContext()
  const [inputMode, setInputMode] = useState<'url' | 'file'>('url')
  const [fileName, setFileName] = useState<string | null>(null)
  // eslint-disable-next-line camelcase
  const toolSource = useWatch({ name: 'tool_source' }) as string | undefined

  if (toolSource !== 'openapi') return null

  const handleModeChange = (_: unknown, newMode: 'url' | 'file' | null) => {
    if (!newMode) return
    setInputMode(newMode)
    if (newMode === 'url') {
      setValue('openapi_content', undefined)
      setFileName(null)
    } else {
      setValue('openapi_url', undefined)
    }
  }

  const handleFileChange = (e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (!file) return
    setFileName(file.name)
    const reader = new FileReader()
    reader.onload = (event) => {
      const result = (event.target as FileReader).result
      if (typeof result === 'string') {
        setValue('openapi_content', result)
      }
    }
    reader.readAsText(file)
  }

  if (showToggle) {
    return (
      <Box sx={{ width: '100%', mb: 1 }}>
        <ToggleButtonGroup
          value={inputMode}
          exclusive
          onChange={handleModeChange}
          size="small"
          sx={{ mb: 1 }}
        >
          <ToggleButton value="url">
            <LinkIcon sx={{ mr: 0.5 }} fontSize="small" />
            {translate('data.specInputUrl')}
          </ToggleButton>
          <ToggleButton value="file">
            <UploadFileIcon sx={{ mr: 0.5 }} fontSize="small" />
            {translate('data.specInputFile')}
          </ToggleButton>
        </ToggleButtonGroup>

        {inputMode === 'url' ? (
          <TextInput
            source="openapi_url"
            label={translate('data.openapiUrl')}
            fullWidth
            helperText={translate('data.openapiUrlHelp')}
          />
        ) : (
          <Box>
            <Button
              variant="outlined"
              component="label"
              startIcon={<UploadFileIcon />}
            >
              {translate('data.selectSpecFile')}
              <input
                type="file"
                hidden
                accept=".json,.yaml,.yml"
                onChange={handleFileChange}
              />
            </Button>
            {fileName && (
              <Typography variant="body2" sx={{ mt: 1 }}>
                {fileName}
              </Typography>
            )}
          </Box>
        )}
      </Box>
    )
  }

  return (
    <Box sx={{ width: '100%' }}>
      <TextInput
        source="openapi_url"
        label={translate('data.openapiUrl')}
        fullWidth
      />
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
        <Button
          variant="outlined"
          component="label"
          size="small"
          startIcon={<UploadFileIcon />}
        >
          {translate('data.uploadSpecFile')}
          <input
            type="file"
            hidden
            accept=".json,.yaml,.yml"
            onChange={handleFileChange}
          />
        </Button>
        {fileName && (
          <Typography variant="body2">{fileName}</Typography>
        )}
      </Box>
    </Box>
  )
}

export default OpenApiUrlField
